// Guest-pass owner slice (TA3). Kept SEPARATE from customerUsersSlice (file-size
// + concern split, per spec §TA3.1). Mirrors the customerUsers slice conventions:
// shared `loading` blanks the list on any mutation, `success` is a
// NotificationMessage the shared ActionToast + getNotificationMessage render.
import { createAsyncThunk, createSlice, PayloadAction, SerializedError } from "@reduxjs/toolkit";
import guestPassesApi from "./api/guestPassesApi";
import { GuestPass, IssueGuestPass } from "./models/guestPassModels";

interface GuestPassNotification {
  key: string;
  params?: Record<string, any>;
}

interface GuestPassesState {
  passes: GuestPass[];
  loading: boolean;
  error?: SerializedError;
  success?: string | GuestPassNotification;
  /** DEMO mint refusal, raw server message, surfaced VERBATIM inline in the mint
   *  dialog (spec §3 point 1: "Demo access is not enabled for this company.").
   *  Deliberately NOT the shared `error` — that one renders as a generic toast on
   *  the pass list, which would swallow the loud refusal the spec requires. */
  demoError?: string;
  /** The just-minted demo pass, held ONLY while the handoff panel is open.
   *  Carries `pinCode` when the server returns it — never persisted, cleared on
   *  dialog close. */
  lastDemoPass?: GuestPass;
  /** GP-MENU §7.1 — the pass returned by "Nový PIN", held ONLY while the
   *  regenerate handoff dialog is open. Exact mirror of `lastDemoPass`.
   *
   *  [SEC-REQ GP-6] SHOW-ONCE IS STRUCTURAL, NOT A CONVENTION: this is the ONLY
   *  place a regenerate response may live. `passes` is assigned from exactly one
   *  source — the `getGuestPasses` (GET) payload, which never carries a PIN — so
   *  a regenerate response can never leak into a list row. Do NOT "helpfully"
   *  merge/patch the new row into `passes`; refresh the list from the server
   *  instead (the dialog's onClose does). Cleared by `clearLastRegeneratedPass`. */
  lastRegeneratedPass?: GuestPass;
}

export const getGuestPasses = createAsyncThunk("guestPasses/list", async () => {
  return guestPassesApi.list();
});

export const issueGuestPass = createAsyncThunk(
  "guestPasses/issue",
  async (model: IssueGuestPass) => {
    return guestPassesApi.issue(model);
  }
);

// DEMO mint. Uses rejectWithValue so the dialog gets the server's user-facing
// message as a plain string: core/api.ts's response interceptor already reshapes
// non-bot-proxy failures into `{ message: data.message, code: data.errorCode }`.
export const issueDemoPass = createAsyncThunk<
  GuestPass,
  IssueGuestPass,
  { rejectValue: string }
>("guestPasses/issueDemo", async (model, { rejectWithValue }) => {
  try {
    return await guestPassesApi.issueDemo(model);
  } catch (e: any) {
    const message = typeof e?.message === "string" ? e.message : "";
    return rejectWithValue(message);
  }
});

export const revokeGuestPass = createAsyncThunk("guestPasses/revoke", async (id: string) => {
  await guestPassesApi.revoke(id);
  return id;
});

export const reissueGuestPass = createAsyncThunk("guestPasses/reissue", async (id: string) => {
  return guestPassesApi.reissue(id);
});

// GP-MENU — "Nový PIN", two sibling thunks so the route is chosen from the ROW's
// source and never adapted client-side (the server refuses a cross-route id with
// 1210). Both resolve to a pass that may carry `pinCode` show-once.
export const regenerateGuestPassPin = createAsyncThunk(
  "guestPasses/regeneratePin",
  async (id: string) => {
    return guestPassesApi.regeneratePin(id);
  }
);

export const regenerateDemoPassPin = createAsyncThunk(
  "guestPasses/regenerateDemoPin",
  async (id: string) => {
    return guestPassesApi.regenerateDemoPin(id);
  }
);

const initialState: GuestPassesState = {
  passes: [],
  loading: false,
  error: undefined,
  success: undefined,
};

const guestPassesSlice = createSlice({
  name: "guestPasses",
  initialState,
  reducers: {
    clearGuestPassError: (state) => {
      state.error = undefined;
    },
    clearGuestPassSuccess: (state) => {
      state.success = undefined;
    },
    clearDemoMintError: (state) => {
      state.demoError = undefined;
    },
    /** Drops the minted pass (incl. any `pinCode`) from memory — called when the
     *  demo handoff panel closes. */
    clearLastDemoPass: (state) => {
      state.lastDemoPass = undefined;
    },
    /** [SEC-REQ GP-6] Drops the regenerated pass (incl. its `pinCode`) from memory
     *  — called when the regenerate handoff dialog closes. After this the PIN is
     *  unrecoverable from the store: `passes` never held it (see the field doc). */
    clearLastRegeneratedPass: (state) => {
      state.lastRegeneratedPass = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getGuestPasses.pending, (state) => {
        state.loading = true;
      })
      .addCase(getGuestPasses.fulfilled, (state, { payload }) => {
        state.passes = payload;
        state.loading = false;
      })
      .addCase(getGuestPasses.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(issueGuestPass.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(issueGuestPass.fulfilled, (state, { meta }) => {
        state.loading = false;
        state.success = {
          key: "guestPass.issued",
          params: { guestEmail: meta.arg.guestEmail },
        };
      })
      .addCase(issueGuestPass.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    // DEMO mint: no success toast — the handoff panel IS the confirmation, and a
    // toast would fight the PIN for the owner's attention mid-call.
    builder
      .addCase(issueDemoPass.pending, (state) => {
        state.loading = true;
        state.demoError = undefined;
        state.lastDemoPass = undefined;
      })
      .addCase(issueDemoPass.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.lastDemoPass = payload;
      })
      .addCase(issueDemoPass.rejected, (state, { payload, error }) => {
        state.loading = false;
        // Prefer the server's own wording; fall back to the serialized message so
        // the operator never gets a silent failure.
        state.demoError = payload || error.message || "";
      });

    builder
      .addCase(revokeGuestPass.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(revokeGuestPass.fulfilled, (state) => {
        state.loading = false;
        state.success = { key: "guestPass.revoked" };
      })
      .addCase(revokeGuestPass.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(reissueGuestPass.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(reissueGuestPass.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.success = {
          key: "guestPass.reissued",
          params: { guestEmail: payload.guestEmail },
        };
      })
      .addCase(reissueGuestPass.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    // "Nový PIN" — no success toast: the handoff dialog IS the confirmation and a
    // toast would fight the PIN for the operator's attention (same rationale as
    // the demo mint). Refusals go to the shared `error` toast — unlike the demo
    // MINT, this path has no verbatim-inline requirement.
    //
    // [SEC-REQ GP-6] The fulfilled payload is written to `lastRegeneratedPass`
    // ONLY. `state.passes` is deliberately untouched here — merging the response
    // (which carries `pinCode`) into a list row would defeat show-once. The list
    // is refreshed from the server when the dialog closes.
    const regenPending = (state: GuestPassesState) => {
      state.loading = true;
      state.error = undefined;
      state.lastRegeneratedPass = undefined;
    };
    const regenFulfilled = (state: GuestPassesState, payload: GuestPass) => {
      state.loading = false;
      state.lastRegeneratedPass = payload;
    };
    const regenRejected = (state: GuestPassesState, error: SerializedError) => {
      state.error = error;
      state.loading = false;
    };

    builder
      .addCase(regenerateGuestPassPin.pending, (state) => regenPending(state))
      .addCase(regenerateGuestPassPin.fulfilled, (state, { payload }) =>
        regenFulfilled(state, payload)
      )
      .addCase(regenerateGuestPassPin.rejected, (state, { error }) =>
        regenRejected(state, error)
      );

    builder
      .addCase(regenerateDemoPassPin.pending, (state) => regenPending(state))
      .addCase(regenerateDemoPassPin.fulfilled, (state, { payload }) =>
        regenFulfilled(state, payload)
      )
      .addCase(regenerateDemoPassPin.rejected, (state, { error }) =>
        regenRejected(state, error)
      );
  },
});

export const {
  clearGuestPassError,
  clearGuestPassSuccess,
  clearDemoMintError,
  clearLastDemoPass,
  clearLastRegeneratedPass,
} = guestPassesSlice.actions;

export default guestPassesSlice.reducer;
